// Geometry for the skirting, dado rail and cornice `LevelRenderer` generates
// from a wall's own extents, so a document only says "trimmed" rather than
// authoring six extra boxes per wall.
//
// It lives in its own module, apart from the renderer that draws it, because
// the one thing worth checking about trim is its measurements, and a test
// should be able to take them without standing up React, three.js and a
// canvas to do it.

import type { MapWallTrimFaces } from '../content/schema/map';

/** How far a trim member stands proud of the wall face, in metres. */
export const TRIM_PROJECTION = 0.03;
/** Points sampled along the moulding's own quarter-round curve. */
const PROFILE_CURVE_SEGMENTS = 8;
/** Skirting board height, in metres. */
export const SKIRTING_HEIGHT = 0.16;
/** Cornice height, in metres. */
export const CORNICE_HEIGHT = 0.1;
/** Dado/picture rail height, in metres. */
export const DADO_HEIGHT = 0.06;

/** One trim member's footprint on the wall's own local X/Z axes. */
export interface WallTrimRun {
  /** Box size on local X and Z. Its height is the member's own. */
  size: [number, number];
  /** Box centre, offset from the wall's own centre on local X and Z. */
  offset: [number, number];
}

/**
 * The footprint of one trim member on a wall of `size`.
 *
 * Absent `faces` (and for `'both'`) this is the geometry trim has always
 * had: the wall's own `size[0]` along the run, and `size[2]` plus a
 * projection at each end of local Z. That is deliberately kept byte for byte,
 * because four other packages' maps are drawn by it - a wall whose trim
 * quietly moved 30 mm would change every room in every one of them.
 *
 * A one-sided run is the opt-in (see `MapWallTrimFaces`): the wall's
 * thickness plus a single projection on the named side, and the full run
 * length on the other axis with no overhang past the wall's ends. The axis
 * comes from the caller rather than from comparing `size[0]` against
 * `size[2]`, so a wall authored thin on either axis is handled, and a square
 * pier is not guessed at.
 */
export function wallTrimRun(
  size: readonly [number, number, number],
  faces?: MapWallTrimFaces,
): WallTrimRun {
  const [width, , depth] = size;
  if (!faces || faces === 'both') {
    return { size: [width, depth + TRIM_PROJECTION * 2], offset: [0, 0] };
  }
  const sign = faces.startsWith('+') ? 1 : -1;
  if (faces === '+x' || faces === '-x') {
    return {
      size: [width + TRIM_PROJECTION, depth],
      offset: [(sign * TRIM_PROJECTION) / 2, 0],
    };
  }
  return {
    size: [width, depth + TRIM_PROJECTION],
    offset: [0, (sign * TRIM_PROJECTION) / 2],
  };
}

/**
 * The moulding's own cross-section, as points in a plane whose X axis is
 * projection off the wall and whose Y axis is height up the member, both
 * relative to the member's own base at the wall - see `MapWallTrimProfile`.
 *
 * `'ovolo'` is a flat foot standing the full `projection` proud of the wall,
 * holding that projection up to `height - projection`, then a quarter-round
 * arc easing back to flush with the wall at the member's own top. The arc is
 * tangent to the foot at its own start (vertical, matching the foot's own
 * run) and tangent to the wall at its own end (horizontal, matching flush
 * with the wall), so the whole outline is C1 continuous - no kink for a
 * grazing light to catch, which is the entire point of having a profile.
 *
 * Degenerates to a plain rectangle when `height <= projection`, since a
 * member too short to hold a foot below the curve has nothing left to be
 * a foot: the flat run collapses to zero and the arc is scaled to fit.
 */
export function wallTrimProfilePoints(
  height: number,
  projection: number,
  segments = PROFILE_CURVE_SEGMENTS,
): Array<[number, number]> {
  const flatHeight = Math.max(0, height - projection);
  const points: Array<[number, number]> = [
    [0, 0],
    [projection, 0],
    [projection, flatHeight],
  ];
  for (let i = 1; i <= segments; i += 1) {
    const angle = (i / segments) * (Math.PI / 2);
    points.push([projection * Math.cos(angle), flatHeight + projection * Math.sin(angle)]);
  }
  return points;
}
